import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Taxonomía de estados del módulo
const STATUS_MAP = {
  listo: {
    label: 'Listo',
    color: 'success',
    hex: '#4caf50',
    icon: CheckCircleIcon,
  },
  en_revisión: {
    label: 'En revisión',
    color: 'warning',
    hex: '#ff9800',
    icon: WarningIcon,
  },
  sin_asignar: {
    label: 'Sin asignar',
    color: 'error',
    hex: '#f44336',
    icon: HelpOutlineIcon,
  },
};

const DEFAULT_STATUS = {
  label: 'Desconocido',
  color: 'default',
  hex: '#9e9e9e',
  icon: ErrorIcon,
};

// Normalizar status: "en_revision" → "en_revisión"
export const normalizeStatus = (status) => {
  if (status === 'en_revision') return 'en_revisión';
  return status;
};

export const getStatusConfig = (status) => {
  return STATUS_MAP[normalizeStatus(status)] || DEFAULT_STATUS;
};

export const getStatusColor = (status) => {
  return (STATUS_MAP[normalizeStatus(status)] || DEFAULT_STATUS).color;
};

export const getStatusHex = (status) => {
  return (STATUS_MAP[normalizeStatus(status)] || DEFAULT_STATUS).hex;
};

export const getStatusLabel = (status) => {
  return (STATUS_MAP[normalizeStatus(status)] || DEFAULT_STATUS).label;
};

export const getStatusIcon = (status) => {
  return (STATUS_MAP[normalizeStatus(status)] || DEFAULT_STATUS).icon;
};

// Umbrales de confianza
export const getConfidenceColor = (percentage) => {
  if (percentage === null || percentage === undefined || percentage === 0) {
    return '#9e9e9e'; // gris
  }
  if (percentage >= 80) return '#4caf50'; // verde
  if (percentage >= 50) return '#ff9800'; // ámbar
  return '#f44336'; // rojo
};

export const getConfidenceLevel = (percentage) => {
  if (percentage === null || percentage === undefined || percentage === 0) {
    return 'none';
  }
  if (percentage >= 80) return 'high';
  if (percentage >= 50) return 'medium';
  return 'low';
};

// Cálculo de estado a nivel documento
// Regla de prioridad: sin_asignar > en_revisión > listo
export const calculateDocumentStatus = (document) => {
  if (!document || !document.orchards) return 'sin_asignar';

  let hasUnassigned = false;
  let hasReview = false;

  document.orchards.forEach((orchard) => {
    if (!orchard.orchardValue) {
      hasUnassigned = true;
      return;
    }

    if (orchard.confidencePercentageOrchard < 50) {
      hasReview = true;
    }

    if (orchard.cc) {
      orchard.cc.forEach((ccItem) => {
        if (ccItem.quarters) {
          ccItem.quarters.forEach((quarter) => {
            if (!quarter.quarterValue) {
              hasUnassigned = true;
            } else if (quarter.confidencePercentageQuarter < 50) {
              hasReview = true;
            }
          });
        }
      });
    }
  });

  if (hasUnassigned) return 'sin_asignar';
  if (hasReview) return 'en_revisión';
  return 'listo';
};

// Recalcular resumen a nivel de filas aplanadas (para Step 2)
// totalPdfs cuenta documentos únicos, los demás contadores cuentan filas
export const recalculateRowSummary = (flatRows, documents) => {
  if (!flatRows || !flatRows.length) {
    return { totalPdfs: 0, listo: 0, enRevision: 0, sinAsignar: 0 };
  }

  const summary = {
    totalPdfs: flatRows.length,
    listo: 0,
    enRevision: 0,
    sinAsignar: 0,
  };

  flatRows.forEach((row) => {
    const status = normalizeStatus(row.processingStatus || 'sin_asignar');
    switch (status) {
      case 'listo':
        summary.listo++;
        break;
      case 'en_revisión':
        summary.enRevision++;
        break;
      case 'sin_asignar':
      default:
        summary.sinAsignar++;
    }
  });

  return summary;
};

// Recalcular resumen de todos los documentos
// totalPdfs cuenta el total de filas (quarters), no de documentos agrupados
export const recalculateSummary = (documents) => {
  if (!documents || !documents.length) {
    return { totalPdfs: 0, listo: 0, enRevision: 0, sinAsignar: 0 };
  }

  const summary = {
    totalPdfs: 0,
    listo: 0,
    enRevision: 0,
    sinAsignar: 0,
  };

  documents.forEach((doc) => {
    if (!doc.orchards || !doc.orchards.length) {
      summary.totalPdfs++;
      const status = normalizeStatus(doc.processingStatus || 'sin_asignar');
      if (status === 'listo') summary.listo++;
      else if (status === 'en_revisión') summary.enRevision++;
      else summary.sinAsignar++;
      return;
    }

    doc.orchards.forEach((orch) => {
      if (!orch.cc || !orch.cc.length) {
        summary.totalPdfs++;
        const status = normalizeStatus(doc.processingStatus || 'sin_asignar');
        if (status === 'listo') summary.listo++;
        else if (status === 'en_revisión') summary.enRevision++;
        else summary.sinAsignar++;
        return;
      }

      orch.cc.forEach((ccItem) => {
        if (!ccItem.quarters || !ccItem.quarters.length) {
          summary.totalPdfs++;
          const status = normalizeStatus(doc.processingStatus || 'sin_asignar');
          if (status === 'listo') summary.listo++;
          else if (status === 'en_revisión') summary.enRevision++;
          else summary.sinAsignar++;
          return;
        }

        ccItem.quarters.forEach((q) => {
          summary.totalPdfs++;
          const status = normalizeStatus(q.processingStatus || doc.processingStatus || 'sin_asignar');
          if (status === 'listo') summary.listo++;
          else if (status === 'en_revisión') summary.enRevision++;
          else summary.sinAsignar++;
        });
      });
    });
  });

  return summary;
};
